import { RefObject, useContext, useMemo, useState } from "react";
import { combineLatest } from "rxjs";
import useResizeObserver from "use-resize-observer";
import throttle from "lodash.throttle";
import * as d3 from "d3";
import { distinctUntilChanged, distinctUntilKeyChanged, filter, map, pairwise, pluck, scan, startWith } from "rxjs/operators";
import { useGroupAxisRowMinWidth } from "./group-axis-row-min-width-hook";
import { CalendarContext, ContextPropsData, TIME_AXIS_WIDTH, ViewportSizeData } from "./calendar-context";

export const usePropsStream = () => useContext(CalendarContext).calendarProps$;

export const useViewportStream = () => useContext(CalendarContext).viewportSize$;

export const useZoomStream = () => useContext(CalendarContext).zoom$;

export const useGroupAxisHeight = () => useContext(CalendarContext).groupAxisHeight$;

export const useUpdateTimeScale = () => useContext(CalendarContext).updateTimeScale;

export const useUpdateGroupScale = () => useContext(CalendarContext).updateGroupScale;

export const useTimeScale = () => useContext(CalendarContext).timeScale$;

export const useGroupScale = () => useContext(CalendarContext).groupScale$;

export const useUpdateGroupAxisHeight = () => useContext(CalendarContext).updateGroupAxisHeight;

export const useCursor = () => useContext(CalendarContext).cursor$;

export const useCalendarState = () => useContext(CalendarContext).state$;

export const useCalendarTheme = () => useContext(CalendarContext).getTheme;

export const useUpdateCursor = () => {
    const ctx = useContext(CalendarContext);
    return ctx.updateCursor.bind(ctx);
};

export const useZoomLevelStream = () => {
    const zoom$ = useZoomStream();
    return useMemo(
        () =>
            zoom$.pipe(
                filter<d3.D3ZoomEvent<SVGGraphicsElement, unknown>>(Boolean),
                map((event) => event.transform.k)
            ),
        [zoom$]
    );
};

/**
 * Throttled element resize observer
 * @param wait Throttle wait time
 * @param providedRef Optionally provide an existing reference for an element to listen to resizes on
 * @returns The existing or new ref, as well as the size of the element
 */
export const useThrottledResizeObserver = <T extends HTMLElement>(wait: number, providedRef?: RefObject<T>) => {
    const [size, setSize] = useState<{ height?: number; width?: number }>({});
    const onResize = useMemo(() => throttle(setSize, wait, { leading: true }), [wait]);
    const { ref } = useResizeObserver<T>({ onResize, ref: providedRef });

    return { ref, ...size };
};

export const useGroupAxisScrollOffset = () => {
    const zoom$ = useZoomStream();
    const viewport$ = useViewportStream();
    const minWidth$ = useGroupAxisRowMinWidth();
    // horizontal scroll is calculated as a translation along the x axis and it relies on viewport size
    // and minimum the width of the group axis which in turn depends on min width of its cells
    return useMemo(() => {
        // stream of panning events. To track horizontal panning we filter out mousemove events and then reduce the horizontal movement of the mouse to an accumulator.
        // The reduced value is the requested scroll by the user. Scan operator emits every new value from the reducer.
        // TODO: optimize number of values emitted to increase performance

        // horizontal scroll on touch devices is implemented by pairing up touchmove events and calculating the delta of the touch move across the page
        // touchstart event is used to separate two different scroll swipes
        const horizontalScroll = zoom$.pipe(
            filter<d3.D3ZoomEvent<SVGGraphicsElement, unknown>>((event) => ["touchmove", "touchstart", "mousemove", "mousedown"].includes(event?.sourceEvent?.type)),
            pairwise(),
            map(([prevEvent, event]) => {
                if (prevEvent?.sourceEvent?.type === "touchmove" && event?.sourceEvent?.type === "touchmove") {
                    return event.sourceEvent.touches[0].pageX - prevEvent.sourceEvent.touches[0].pageX;
                }
                if (prevEvent?.sourceEvent?.type === "mousemove" && event?.sourceEvent?.type === "mousemove") {
                    return event.sourceEvent.pageX - prevEvent.sourceEvent.pageX;
                }
                return 0;
            }),
            startWith(0),
            distinctUntilChanged()
        );

        return combineLatest([horizontalScroll, viewport$.pipe(filter<ViewportSizeData>(Boolean), distinctUntilKeyChanged("width")), minWidth$]).pipe(
            scan((acc, [scrollIncrement, { width }, rowWidth]) => {
                const scrollWidth = Math.abs(width - rowWidth - TIME_AXIS_WIDTH);
                const newScrollOffset = acc - scrollIncrement;
                return newScrollOffset > 0 ? Math.min(newScrollOffset, scrollWidth) : 0;
            }, 0),
            distinctUntilChanged()
        );
    }, [zoom$, viewport$, minWidth$]);
};

export const useShowSubEvents = () => {
    const props$ = usePropsStream();

    return useMemo(() => props$.pipe(filter<ContextPropsData>(Boolean), distinctUntilKeyChanged("showSubEvents"), pluck("showSubEvents")), [props$]);
};
